import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";
import { findLatestDocumentVersion } from "../../../api/content/sourcesApi";
import { appPaths, segmentPathOfFragment } from "../../../shared/routing/appPaths";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import { parseDocumentAddress } from "../../../shared/references/contentReferences";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import DocumentAtVersion from "./DocumentAtVersion";

/**
 * `/sources/<document id>[@<version>][#<segment path>]`. An address without a version is
 * resolved to the latest version and replaced by the exact one, so what the address bar
 * shows can be shared and opens the same text later (NFR-16).
 */
export default function SourceDocumentPage() {
  const { documentAddress: segment } = useParams();
  const location = useLocation();
  const address = parseDocumentAddress(segment);
  const segmentPath = segmentPathOfFragment(location.hash);

  if (address === null) {
    return (
      <div className="p-4">
        <ErrorNotice error={{ kind: "invalidAddress", address: segment ?? "" }} />
      </div>
    );
  }
  if (address.version === null) {
    return <LatestDocumentVersion documentId={address.documentId} segmentPath={segmentPath} />;
  }
  return (
    <DocumentAtVersion reference={{ documentId: address.documentId, version: address.version }} segmentPath={segmentPath} />
  );
}

function LatestDocumentVersion({ documentId, segmentPath }: { documentId: string; segmentPath: string | null }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const load = useCallback((signal: AbortSignal) => findLatestDocumentVersion(documentId, signal), [documentId]);
  const { state: latest, reload } = useLoad(load);

  useEffect(() => {
    if (latest.status === "loaded") {
      void navigate(appPaths.sourceDocument(latest.value, segmentPath), { replace: true });
    }
  }, [latest, navigate, segmentPath]);

  if (latest.status === "failed") {
    return <ErrorNotice error={latest.error} onRetry={reload} className="p-4" />;
  }
  return <LoadingIndicator label={t("sources.document.findingLatest")} className="py-10" />;
}
